import Avatar from "@/ui/avatar";
import { IconComponent } from "@/ui/icon";
import { useToken } from "../../hooks/use-token";
import { Format } from "@/utils/formatters";
import IconButton from "@/ui/icon-button";
import { useState } from "react";
import { isTokenFavourite, toggleFavouriteToken } from "@utils/token";
import { getSecondsSince } from "@/utils/date";
import { Trend } from "@/ui/formats";

const TokenInfoHeader = () => {
  const token = useToken();

  const [isCopying, setIsCopying] = useState(false);
  const [isFavourite, setIsFavourite] = useState(
    isTokenFavourite(token?.tokenId ?? "")
  );

  if (!token) {
    return null;
  }

  const toggleFavorite = (tokenId: string) => {
    setIsFavourite(!isFavourite);
    toggleFavouriteToken(tokenId);
  };

  return (
    <div className="w-full flex justify-between px-[0.625rem] pt-[0.875rem] pb-[0.625rem]">
      <div className="w-auto">
        <div className="flex items-center gap-[0.3125rem]">
          {token.icon && (
            <div className="w-[1.375rem] h-[1.375rem] flex justify-center items-center">
              <Avatar
                source={token.icon}
                size="token"
                fallbackLetter={token?.symbol?.[0]}
              />
            </div>
          )}
          <div className="flex gap-x-1 text-[0.75rem] leading-[0.75rem]">
            <p className="font-semibold">{token?.pair?.token0Data?.symbol}</p>
            <span className="font-semibold opacity-50">
              {token?.name?.length > 20
                ? token.name.slice(0, 20) + "..."
                : token?.name || ""}
            </span>
          </div>
          <div className="flex justify-start items-center gap-[0.3125rem]">
            <div className="w-[1.375rem] h-[1.375rem] flex justify-center items-center">
              <IconButton
                withHover={true}
                withBorder={false}
                name={isCopying ? "check" : "copyIcon"}
                variant="circle"
                className="w-[1.375rem] h-[1.375rem] bg-black rounded-full"
                iconClassName="w-[0.625rem] h-[0.625rem]"
                onClick={(event) => {
                  event.stopPropagation();
                  setIsCopying(true);
                  navigator.clipboard.writeText(token?.address ?? "");
                  setTimeout(() => {
                    setIsCopying(false);
                  }, 1000);
                }}
              />
            </div>
            <div className="w-[1.375rem] h-[1.375rem] flex justify-center items-center">
              <IconButton
                withHover={true}
                withBorder={false}
                name={isFavourite ? "starFull" : "starEmpty"}
                variant="circle"
                className="w-[1.375rem] h-[1.375rem] bg-black rounded-full"
                iconClassName="w-[0.625rem] h-[0.625rem]"
                onClick={(event) => {
                  event.stopPropagation();
                  toggleFavorite(token?.tokenId ?? "");
                }}
              />
            </div>
            {token.socialLinks?.website &&
              token.socialLinks.website.trim() !== "" && (
                <div className="w-[1.375rem] h-[1.375rem] flex justify-center items-center">
                  <IconButton
                    withHover={true}
                    withBorder={false}
                    name="internetPlanet"
                    variant="circle"
                    className="w-[1.375rem] h-[1.375rem] bg-black rounded-full"
                    iconClassName="w-[0.625rem] h-[0.625rem]"
                    onClick={() =>
                      window.open(token.socialLinks.website, "_blank")
                    }
                  />
                </div>
              )}
            {token.socialLinks?.twitter &&
              token.socialLinks.twitter.trim() !== "" && (
                <div className="w-[1.375rem] h-[1.375rem] flex justify-center items-center">
                  <IconButton
                    withHover={true}
                    withBorder={false}
                    name="socialX"
                    variant="circle"
                    className="w-[1.375rem] h-[1.375rem] bg-black rounded-full"
                    iconClassName="w-[0.5rem] h-[0.5rem]"
                    onClick={() =>
                      window.open(token.socialLinks.twitter, "_blank")
                    }
                  />
                </div>
              )}
            {token.socialLinks?.telegram &&
              token.socialLinks.telegram.trim() !== "" && (
                <div className="w-[1.375rem] h-[1.375rem] flex justify-center items-center">
                  <IconButton
                    withHover={true}
                    withBorder={false}
                    name="socialTelegram"
                    variant="circle"
                    className="w-[1.375rem] h-[1.375rem] bg-black rounded-full"
                    iconClassName="w-[0.625rem] h-[0.625rem]"
                    onClick={() =>
                      window.open(token.socialLinks.telegram, "_blank")
                    }
                  />
                </div>
              )}
          </div>
          <div className="items-center justify-end gap-[0.3125rem] hidden">
            <div className="w-[1.375rem] h-[1.375rem] flex justify-center items-center bg-black rounded-full">
              <IconComponent
                name="markBlue"
                className="w-[0.75rem] h-[0.75rem]"
              />
            </div>
            <div className="w-[1.375rem] h-[1.375rem] flex justify-center items-center bg-black rounded-full">
              <IconComponent
                name="markPurple"
                className="w-[0.625rem] h-[0.625rem]"
              />
            </div>
          </div>
        </div>
      </div>
      <div className="h-[1.375rem] flex items-center justify-center gap-[0.625rem]">
        <div className="text-xs leading-[0.875rem] flex flex-col justify-center items-center">
          <p className="font-semibold">Age</p>
          <span className="font-normal opacity-50">
            {Format.from(getSecondsSince(Date.parse(token.age)))
              .time({ variant: "short" })
              .toString()}
          </span>
        </div>
        <div className="text-xs leading-[0.875rem] flex flex-col justify-center items-center">
          <p className="font-semibold">Txns</p>
          <span className="font-normal opacity-50">
            {Format.from(Number(token.txns)).unit().toString()}
          </span>
        </div>
        <div className="text-xs leading-[0.875rem] flex flex-col justify-center items-center">
          <p className="font-semibold">Wallets</p>
          <span className="font-normal opacity-50">
            {Format.from(Number(token.makers)).unit().toString()}
          </span>
        </div>
        <div className="text-xs leading-[0.875rem] flex flex-col justify-center items-center">
          <p className="font-semibold">Holders</p>
          <span className="font-normal opacity-50">
            {Format.from(Number(token.holders)).unit().toString()}
          </span>
        </div>
        <div className="text-xs leading-[0.875rem] flex flex-col justify-center items-center">
          <p className="font-semibold">Volume</p>
          <span className="font-normal opacity-50">
            ${Format.from(Number(token.volume)).unit().toString()}
          </span>
        </div>
        <div className="text-xs leading-[0.875rem] flex flex-col justify-center items-center">
          <p className="font-semibold">Liquidity</p>
          <span className="font-normal opacity-50">
            ${Format.from(Number(token.liquidity)).unit().toString()}
          </span>
        </div>
        <div className="text-xs leading-[0.875rem] flex flex-col justify-center items-center">
          <p className="font-semibold">Market Cap</p>
          <span className="font-normal opacity-50">
            ${Format.from(Number(token.marketCap)).unit().toString()}
          </span>
        </div>
        <div className="text-xs leading-[0.875rem] flex flex-col justify-center items-center">
          <p className="font-semibold">Price</p>
          <span className="font-normal opacity-50">
            $
            {Format.from(Number(token.price))
              .unit(Number(token.price) !== 0, 7, false)
              .toString()}
          </span>
        </div>
        <div className="text-xs leading-[0.875rem] flex flex-col justify-center items-center">
          <p className="font-semibold">1H</p>
          <span className="font-normal">
            <Trend
              value={Number(token.priceChange1Hour)}
              withSign={false}
              withTrendArrow={true}
              smallSpace
            />
          </span>
        </div>
        <div className="text-xs leading-[0.875rem] flex flex-col justify-center items-center">
          <p className="font-semibold">24H</p>
          <span className="font-normal">
            <Trend
              value={Number(token.priceChange24Hours)}
              withSign={false}
              withTrendArrow={true}
              smallSpace
            />
          </span>
        </div>
        <div className="text-xs leading-[0.875rem] flex flex-col justify-center items-center">
          <p className="font-semibold">Exchange</p>
          <div className="w-[0.9375rem] h-[0.9775rem] flex justify-center items-center">
            <Avatar source={token.exchange} size="small" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TokenInfoHeader;
